import React from 'react';
import { ShoppingCart, X, Trash2, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { useOptionsBasket, OPTIONS_ACCOUNT } from './OptionsBasketContext';
import './OptionsBasketDrawer.css';

const OptionsBasketDrawer = () => {
    const {
        basketItems, removeFromBasket, updateItem, clearBasket, placeAll,
        isPlacing, placeResults, isDrawerOpen, setIsDrawerOpen, toast, prices,
    } = useOptionsBasket();

    const totalPremium = basketItems.reduce((sum, item) => {
        const price = prices[item.tradingsymbol];
        if (price === undefined) return sum;
        return sum + price * (item.lots || 0) * item.lot_size;
    }, 0);
    const allPriced = basketItems.every(item => prices[item.tradingsymbol] !== undefined);

    return (
        <>
            {isDrawerOpen && (
                <div className="opt-drawer-overlay" onClick={() => setIsDrawerOpen(false)}>
                    <div className="opt-drawer" onClick={(e) => e.stopPropagation()}>
                        <div className="opt-drawer-header">
                            <div className="opt-drawer-title">
                                <span>Basket</span>
                                <span className="opt-basket-count">{basketItems.length}</span>
                            </div>
                            <div className="opt-drawer-header-actions">
                                <span className="opt-basket-account-badge">{OPTIONS_ACCOUNT}</span>
                                <button className="opt-drawer-close" onClick={() => setIsDrawerOpen(false)}><X size={16} /></button>
                            </div>
                        </div>

                        <div className="opt-drawer-body">
                            {basketItems.length === 0 ? (
                                <div className="opt-basket-empty">
                                    <ShoppingCart size={22} strokeWidth={1.5} />
                                    <p>Basket is empty</p>
                                    <span>Use B / S / + on any strike in the option chain to add a leg</span>
                                </div>
                            ) : (
                                <div className="opt-leg-list">
                                    {basketItems.map((item) => (
                                        <div key={item.id} className={`opt-leg-item opt-leg-${item.instrument_type.toLowerCase()}`}>
                                            <div className="opt-leg-item-top">
                                                <div className="opt-leg-symbol-row">
                                                    <span className={`opt-type-badge ${item.instrument_type.toLowerCase()}`}>{item.instrument_type}</span>
                                                    <div className="opt-leg-symbol-text">
                                                        <strong>{item.tradingsymbol}</strong>
                                                        <div className="opt-leg-meta">
                                                            {item.exchange} · {item.expiry} · {item.strike}
                                                            {' · LTP '}
                                                            {prices[item.tradingsymbol] !== undefined ? `₹${prices[item.tradingsymbol]}` : '…'}
                                                            {prices[item.tradingsymbol] !== undefined && (
                                                                <> {' · Total ₹'}{(prices[item.tradingsymbol] * (item.lots || 0) * item.lot_size).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                <button className="opt-basket-remove-btn" onClick={() => removeFromBasket(item.id)} title="Remove">
                                                    <X size={14} />
                                                </button>
                                            </div>

                                            <div className="opt-leg-controls">
                                                <div className="opt-toggle-group">
                                                    <button
                                                        className={`opt-toggle ${item.transaction_type === 'BUY' ? 'active-buy' : ''}`}
                                                        onClick={() => updateItem(item.id, 'transaction_type', 'BUY')}
                                                    >BUY</button>
                                                    <button
                                                        className={`opt-toggle ${item.transaction_type === 'SELL' ? 'active-sell' : ''}`}
                                                        onClick={() => updateItem(item.id, 'transaction_type', 'SELL')}
                                                    >SELL</button>
                                                </div>
                                                <div className="opt-toggle-group">
                                                    <button
                                                        className={`opt-toggle ${item.product === 'MIS' ? 'active' : ''}`}
                                                        onClick={() => updateItem(item.id, 'product', 'MIS')}
                                                    >MIS</button>
                                                    <button
                                                        className={`opt-toggle ${item.product === 'NRML' ? 'active' : ''}`}
                                                        onClick={() => updateItem(item.id, 'product', 'NRML')}
                                                    >NRML</button>
                                                </div>
                                                <div className="opt-toggle-group">
                                                    <button
                                                        className={`opt-toggle ${item.order_type === 'MARKET' ? 'active' : ''}`}
                                                        onClick={() => updateItem(item.id, 'order_type', 'MARKET')}
                                                    >MKT</button>
                                                    <button
                                                        className={`opt-toggle ${item.order_type === 'LIMIT' ? 'active' : ''}`}
                                                        onClick={() => {
                                                            updateItem(item.id, 'order_type', 'LIMIT');
                                                            if (!item.price && prices[item.tradingsymbol] !== undefined) {
                                                                updateItem(item.id, 'price', prices[item.tradingsymbol]);
                                                            }
                                                        }}
                                                    >LMT</button>
                                                </div>
                                                {item.order_type === 'LIMIT' && (
                                                    <label className="opt-leg-mini-field">
                                                        <span>Price</span>
                                                        <input
                                                            type="number"
                                                            className="opt-leg-mini-input"
                                                            step="0.05"
                                                            value={item.price || ''}
                                                            onChange={(e) => updateItem(item.id, 'price', e.target.value)}
                                                        />
                                                    </label>
                                                )}
                                                <label className="opt-leg-mini-field">
                                                    <span>Lots</span>
                                                    <input
                                                        type="number"
                                                        className="opt-leg-mini-input"
                                                        min="1"
                                                        step="1"
                                                        value={item.lots}
                                                        onChange={(e) => updateItem(item.id, 'lots', e.target.value)}
                                                    />
                                                </label>
                                                <span className="opt-leg-lot-hint">× {item.lot_size} = {(item.lots || 0) * item.lot_size}</span>
                                                <label className="opt-leg-mini-field">
                                                    <span>SL pts</span>
                                                    <input
                                                        type="number"
                                                        className="opt-leg-mini-input"
                                                        min="1"
                                                        step="1"
                                                        value={item.sl_points}
                                                        onChange={(e) => updateItem(item.id, 'sl_points', e.target.value)}
                                                    />
                                                </label>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {basketItems.length > 0 && (
                                <button className="opt-basket-clear-btn opt-drawer-clear" onClick={clearBasket}>
                                    <Trash2 size={12} /> Clear basket
                                </button>
                            )}

                            {placeResults && (
                                <div className="opt-basket-results">
                                    {placeResults.results && placeResults.results.length > 0 && (
                                        <div className="opt-results-section">
                                            <h4><CheckCircle2 size={13} /> Placed</h4>
                                            {placeResults.results.map((r, idx) => (
                                                <div key={idx} className="opt-result-row success">
                                                    <CheckCircle2 size={14} className="opt-result-icon" />
                                                    <div className="opt-result-body">
                                                        <strong>{r.symbol}</strong>
                                                        <span>Entry #{r.entry_order_id} @ ₹{r.entry_price}</span>
                                                        <span>SL #{r.sl_order_id} @ ₹{r.sl_trigger_price}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {placeResults.errors && placeResults.errors.length > 0 && (
                                        <div className="opt-results-section">
                                            <h4><XCircle size={13} /> Failed</h4>
                                            {placeResults.errors.map((r, idx) => (
                                                <div key={idx} className="opt-result-row error">
                                                    <XCircle size={14} className="opt-result-icon" />
                                                    <div className="opt-result-body">
                                                        <strong>{r.symbol}</strong>
                                                        <span>{r.error}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {basketItems.length > 0 && (
                            <div className="opt-drawer-footer">
                                <div className="opt-drawer-footer-left">
                                    <span className="opt-drawer-total">
                                        Total {allPriced ? `₹${totalPremium.toLocaleString('en-IN', { maximumFractionDigits: 2 })}` : '…'}
                                    </span>
                                    <span className="opt-drawer-footer-hint">Each leg places an entry order + a broker SL-M order</span>
                                </div>
                                <button className="opt-basket-place-btn" onClick={placeAll} disabled={isPlacing}>
                                    {isPlacing && <Loader2 size={15} className="opt-spin" />}
                                    {isPlacing ? 'Placing…' : `Place ${basketItems.length} Leg${basketItems.length > 1 ? 's' : ''}`}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {toast.show && (
                <div className={`opt-basket-notification ${toast.type}`}>
                    {toast.type === 'success' ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
                    <span>{toast.message}</span>
                </div>
            )}
        </>
    );
};

export default OptionsBasketDrawer;
